import React from 'react';
import { Phone, Mail, MessageSquare, DollarSign, Calendar } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const ConversionTracking = ({ analyticsData, onRefresh }) => {
  // Filter conversion events
  const conversions = analyticsData?.filter(event => event?.conversion_tracked) || [];
  
  // Calculate conversion metrics
  const conversionMetrics = {
    total: conversions?.length,
    phoneInquiries: conversions?.filter(e => e?.event_name?.includes('phone') || e?.event_name?.includes('call'))?.length,
    formSubmissions: conversions?.filter(e => e?.event_name?.includes('form'))?.length,
    chatInquiries: conversions?.filter(e => e?.event_name?.includes('chat'))?.length,
    totalValue: conversions?.reduce((sum, e) => sum + (parseFloat(e?.event_value) || 0), 0)
  };

  const conversionTypes = [
    {
      title: 'Phone Inquiries',
      count: conversionMetrics?.phoneInquiries,
      icon: Phone,
      color: 'blue',
      description: 'Direct call conversions'
    },
    {
      title: 'Form Submissions',
      count: conversionMetrics?.formSubmissions,
      icon: Mail,
      color: 'green',
      description: 'Contact form conversions'
    },
    {
      title: 'Chat Inquiries',
      count: conversionMetrics?.chatInquiries,
      icon: MessageSquare,
      color: 'purple',
      description: 'Live chat conversions'
    },
    {
      title: 'Total Value',
      count: `$${conversionMetrics?.totalValue?.toLocaleString()}`,
      icon: DollarSign,
      color: 'yellow',
      description: 'Estimated conversion value'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Conversion Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {conversionTypes?.map((type, index) => {
          const Icon = type?.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className={`w-12 h-12 rounded-lg bg-${type?.color}-100 flex items-center justify-center mb-4`}>
                <Icon className={`w-6 h-6 text-${type?.color}-600`} />
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">{type?.title}</h3>
              <p className="text-3xl font-bold text-gray-900 mb-2">{type?.count}</p>
              <p className="text-sm text-gray-500">{type?.description}</p>
            </div>
          );
        })}
      </div>
      {/* Conversion Timeline */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Recent Conversions</h2>
          <button
            onClick={onRefresh}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            Refresh
          </button>
        </div>

        {conversions?.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No conversions tracked yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {conversions?.slice(0, 10)?.map((conversion, index) => {
              const iconMap = {
                phone: Phone,
                form: Mail,
                chat: MessageSquare
              };
              const eventType = conversion?.event_name?.toLowerCase() || '';
              const Icon = Object.keys(iconMap)?.find(key => eventType?.includes(key))
                ? iconMap?.[Object.keys(iconMap)?.find(key => eventType?.includes(key))]
                : DollarSign;

              return (
                <div key={index} className="flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 mb-1">
                      {conversion?.event_category || conversion?.event_name}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(conversion.created_at)?.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      {conversion?.page_path && (
                        <span className="text-blue-600 truncate">{conversion?.page_path}</span>
                      )}
                    </div>
                  </div>
                  {conversion?.event_value && (
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">
                        ${parseFloat(conversion?.event_value)?.toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">Est. value</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Conversion Funnel */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Conversion Funnel</h2>
        
        <div className="space-y-4">
          {[
            { stage: 'Website Visits', count: 5420, percentage: 100 },
            { stage: 'Page Engagement', count: 3250, percentage: 60 },
            { stage: 'Contact Initiated', count: 542, percentage: 10 },
            { stage: 'Conversions', count: conversionMetrics?.total, percentage: 4.3 }
          ]?.map((stage, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-900">{stage?.stage}</span>
                <span className="text-gray-600">{stage?.count?.toLocaleString()} ({stage?.percentage}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-blue-600 h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                  style={{ width: `${stage?.percentage}%` }}
                >
                  {stage?.percentage >= 20 && (
                    <span className="text-xs font-bold text-white">{stage?.percentage}%</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConversionTracking;